"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note } from "./parts";

/**
 * Ce que vaut un utilisateur gratuit, et ce qu'on peut donc dépenser pour lui.
 *
 * Une première version comparait freemium, essai gratuit et payant d'emblée en
 * faisant varier le trafic. Elle ne démontrait rien : les trois revenus étant
 * proportionnels au trafic, le classement ne changeait jamais et le curseur ne
 * faisait que multiplier. Le vrai basculement n'est pas dans le volume mais
 * dans le coût unitaire, et il renverse une intuition répandue : au-dessus du
 * seuil, plus de trafic aggrave la perte au lieu de la combler.
 */

const L = {
    fr: {
        title: "Ce que vous pouvez dépenser pour un utilisateur gratuit",
        conversion: "Gratuits qui finissent par payer",
        price: "Prix mensuel",
        cost: "Coût mensuel d'un utilisateur gratuit",
        worth: "Ce que rapporte un gratuit",
        ceiling: "Au-delà de ce coût, chaque gratuit vous coûte de l'argent",
        result: "Résultat pour 1 000 utilisateurs gratuits",
        perMonth: "par mois",
        brings: "Rapporte",
        costs: "Coûte",
        good: "Chaque utilisateur gratuit reste rentable. Faire venir plus de monde améliore le résultat.",
        warn: "La marge par utilisateur gratuit est très mince. Une hausse de coût, et le modèle bascule.",
        bad: "Chaque utilisateur gratuit vous coûte plus qu'il ne rapporte. Plus de trafic aggrave la perte au lieu de la combler.",
        note: "Le seuil est le prix multiplié par le taux de conversion : c'est la valeur mensuelle moyenne d'un inscrit gratuit, une fois répartie sur tous. C'est le plafond réel de ce que peut coûter le service rendu gratuitement, hébergement et support compris.",
    },
    en: {
        title: "What you can afford to spend on a free user",
        conversion: "Free users who eventually pay",
        price: "Monthly price",
        cost: "Monthly cost of one free user",
        worth: "What one free user brings",
        ceiling: "Above this cost, every free user loses you money",
        result: "Result for 1,000 free users",
        perMonth: "per month",
        brings: "Brings",
        costs: "Costs",
        good: "Every free user stays profitable. Bringing more people in improves the result.",
        warn: "Margin per free user is very thin. One cost increase and the model flips.",
        bad: "Every free user costs more than they bring. More traffic deepens the loss instead of closing it.",
        note: "The threshold is price times conversion rate: the average monthly value of a free signup once spread across everyone. It is the real ceiling on what the free service can cost you, hosting and support included.",
    },
};

export default function FreemiumEconomics({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [conversion, setConversion] = useState(3);
    const [price, setPrice] = useState(19);
    const [cost, setCost] = useState(0.3);

    const r = useMemo(() => {
        const worth = (conversion / 100) * price; // valeur mensuelle d'un gratuit
        const net = worth - cost;
        return {
            worth,
            net,
            brings1000: worth * 1000,
            costs1000: cost * 1000,
            net1000: net * 1000,
            ratio: worth > 0 ? cost / worth : 2,
        };
    }, [conversion, price, cost]);

    const tone = r.net <= 0 ? "bad" : r.ratio > 0.7 ? "warn" : "good";
    const euro = (v, d = 2) => `${v.toFixed(d).replace(".", lang === "fr" ? "," : ".")} €`;
    const big = (v) =>
        `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

    // Les deux barres partagent la même échelle, sinon la comparaison ment.
    const scale = Math.max(r.worth, cost) * 1.1;

    const bar = (label, value, colour) => (
        <div>
            <div className="flex justify-between items-baseline mb-1.5 gap-3">
                <span className="text-sm text-gray-600 dark:text-lightBlue">{label}</span>
                <span className="text-sm font-bold tabular-nums dark:text-white shrink-0">
                    {euro(value)} {t.perMonth}
                </span>
            </div>
            <div className="h-5 rounded-full bg-gray-200/70 dark:bg-white/10 overflow-hidden">
                <div
                    className={`h-full rounded-full ${colour}`}
                    style={{ width: `${Math.min(100, (value / scale) * 100)}%` }}
                />
            </div>
        </div>
    );

    return (
        <Frame icon="calculator-minimalistic-linear" title={t.title}>
            <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-4">
                    <Slider
                        label={t.conversion}
                        value={conversion}
                        onChange={setConversion}
                        min={0.5}
                        max={10}
                        step={0.5}
                        display={`${conversion} %`}
                    />
                    <Slider label={t.price} value={price} onChange={setPrice} min={5} max={99} step={1} display={`${price} €`} />
                    <Slider label={t.cost} value={cost} onChange={setCost} min={0.05} max={3} step={0.05} display={euro(cost)} />
                </div>

                <div className="space-y-5 sm:pt-1">
                    {bar(t.brings, r.worth, "bg-primary")}
                    {bar(t.costs, cost, r.net <= 0 ? "bg-red-500" : "bg-gray-400 dark:bg-white/35")}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.ceiling}</div>
                    <div className="text-3xl font-bold text-primary tabular-nums">{euro(r.worth)}</div>
                </div>
                <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.result}</div>
                    <div
                        className={`text-3xl font-bold tabular-nums ${r.net1000 >= 0 ? "text-green-700 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                    >
                        {r.net1000 >= 0 ? "+" : ""}
                        {big(r.net1000)}
                    </div>
                </div>
            </div>

            <Verdict tone={tone}>{t[tone]}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
